import {
  type AmberdataAnalyticsList,
  extractAmberdataAnalyticsPayload,
  isAmberdataComparisonAnalyticsType,
  isAmberdataRangeAnalyticsType,
} from '@/service/amberdata/analytics.types'
import { orderXClient } from '@/service/client'
import type {
  DataPayload,
  GetAnalyticRangeRequest,
  GetAnalyticSnapshotRequest,
  SurfaceCurrency,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export async function fetchAmberdataAnalyticSnapshot(
  params: GetAnalyticSnapshotRequest,
  signal?: AbortSignal
): Promise<DataPayload> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        getAnalyticSnapshot: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    return data
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchAmberdataAnalyticRange(
  params: GetAnalyticRangeRequest,
  signal?: AbortSignal
): Promise<DataPayload> {
  // Comparison types (Term Structure) support historical range queries for the comparison feature
  if (
    !isAmberdataRangeAnalyticsType(params.analyticsType) &&
    !isAmberdataComparisonAnalyticsType(params.analyticsType)
  ) {
    return Promise.reject({
      status: 400,
      message: 'This Amberdata analytics type only supports snapshot fetching.',
    } satisfies ErrorResponse)
  }

  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        getAnalyticRange: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    return data
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchAmberdataAnalyticSnapshotList(
  params: GetAnalyticSnapshotRequest,
  signal?: AbortSignal
): Promise<AmberdataAnalyticsList> {
  const payload = await fetchAmberdataAnalyticSnapshot(params, signal)
  const list = extractAmberdataAnalyticsPayload(payload, params.analyticsType)

  if (!list) {
    return Promise.reject({
      status: 400,
      message: 'Amberdata snapshot response did not include the requested analytics payload.',
    } satisfies ErrorResponse)
  }

  return list
}

export async function fetchAmberdataAnalyticRangeList(
  params: GetAnalyticRangeRequest,
  signal?: AbortSignal
): Promise<AmberdataAnalyticsList> {
  const payload = await fetchAmberdataAnalyticRange(params, signal)
  const list = extractAmberdataAnalyticsPayload(payload, params.analyticsType)

  if (!list) {
    return Promise.reject({
      status: 400,
      message: 'Amberdata range response did not include the requested analytics payload.',
    } satisfies ErrorResponse)
  }

  return list
}

export async function fetchSurfaceCurrencies(signal?: AbortSignal): Promise<SurfaceCurrency[]> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        surfaceCurrencies: {},
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    return data.surfaceCurrencies?.items ?? []
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
